import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import { alpha } from '@mui/material/styles';
import AddRounded from '@mui/icons-material/AddRounded';
import PersonOutlined from '@mui/icons-material/PersonOutlined';
import HandshakeOutlined from '@mui/icons-material/HandshakeOutlined';
import PhoneOutlined from '@mui/icons-material/PhoneOutlined';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import LocationOnOutlined from '@mui/icons-material/LocationOnOutlined';

import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import StatusBadge from '../components/common/StatusBadge';
import { getMyPortal } from '../services/consultancyService';
import { createStudent } from '../services/studentService';
import { STATUS_COLORS } from '../config/constants';

const ConsultancyPortalPage = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add lead dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getMyPortal();
      setData(res.data?.data || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load portal data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) errors.name = 'Name is required';
    if (!form.phone.trim()) errors.phone = 'Phone is required';
    else if (!/^[0-9+\-\s()]{7,15}$/.test(form.phone.trim()))
      errors.phone = 'Enter a valid phone number';
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errors.email = 'Enter a valid email address';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddLead = async () => {
    if (!validateForm()) return;
    try {
      setSaving(true);
      await createStudent({
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim() || undefined,
      });
      setDialogOpen(false);
      setForm({ name: '', phone: '', email: '' });
      setFormErrors({});
      setSuccessMsg('Lead added successfully');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to add lead');
    } finally {
      setSaving(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setForm({ name: '', phone: '', email: '' });
    setFormErrors({});
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const consultancy = data?.consultancy;
  const students = data?.students || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader title="Consultancy Portal">
        <Button variant="contained" startIcon={<AddRounded />} onClick={() => setDialogOpen(true)}>
          Add New Lead
        </Button>
      </PageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {successMsg && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
          {successMsg}
        </Alert>
      )}

      {/* Consultancy Info */}
      {consultancy && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 2,
                bgcolor: alpha('#0EA5E9', 0.12),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <HandshakeOutlined sx={{ color: '#0EA5E9', fontSize: 26 }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', color: '#1F2937' }}>
                {consultancy.name}
              </Typography>
              {consultancy.contactPerson && (
                <Typography sx={{ fontSize: '0.8rem', color: '#6B7280', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <PersonOutlined sx={{ fontSize: 14 }} /> {consultancy.contactPerson}
                </Typography>
              )}
            </Box>
          </Box>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            {consultancy.phone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <PhoneOutlined sx={{ fontSize: 16, color: '#9CA3AF' }} />
                <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>{consultancy.phone}</Typography>
              </Box>
            )}
            {consultancy.email && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <EmailOutlined sx={{ fontSize: 16, color: '#9CA3AF' }} />
                <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>{consultancy.email}</Typography>
              </Box>
            )}
            {consultancy.address && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <LocationOnOutlined sx={{ fontSize: 16, color: '#9CA3AF' }} />
                <Typography sx={{ fontSize: '0.85rem', color: '#374151' }}>{consultancy.address}</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 140 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
            Total Students
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#2C7BE5' }}>
            {data?.totalStudents || 0}
          </Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 140 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
            Total Payout
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#F59E0B' }}>
            {formatAmount(data?.totalPayout)}
          </Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 140 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
            Paid
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#10B981' }}>
            {formatAmount(data?.paidPayout)}
          </Typography>
        </Paper>
        <Paper sx={{ px: 3, py: 2, flex: 1, minWidth: 140 }}>
          <Typography sx={{ fontSize: '0.72rem', color: '#6B7280', fontWeight: 600, textTransform: 'uppercase', mb: 0.5 }}>
            Pending Payout
          </Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 700, color: '#EF4444' }}>
            {formatAmount(data?.pendingPayout)}
          </Typography>
        </Paper>
      </Box>

      {/* Students Table */}
      <Paper>
        <Box sx={{ px: 3, py: 2, borderBottom: '1px solid #F1F3F9' }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1F2937' }}>
            Your Students ({students.length})
          </Typography>
        </Box>
        {students.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
              No students yet. Click "Add New Lead" to get started.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#F9FAFB' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#6B7280', textTransform: 'uppercase' }}>
                    File No.
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#6B7280', textTransform: 'uppercase' }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#6B7280', textTransform: 'uppercase' }}>
                    Phone
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#6B7280', textTransform: 'uppercase' }}>
                    Course / Country
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#6B7280', textTransform: 'uppercase' }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#6B7280', textTransform: 'uppercase' }}>
                    Payout
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.72rem', color: '#6B7280', textTransform: 'uppercase' }}>
                    Payout Status
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((s) => (
                  <TableRow key={s._id} hover onClick={() => navigate(`/consultancy-portal/${s._id}`)} sx={{ cursor: 'pointer', '&:last-child td': { borderBottom: 0 } }}>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#2C7BE5' }}>
                      {s.fileNumber || '-'}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#2C7BE5' }}>
                        {s.name}
                      </Typography>
                      {s.email && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{s.email}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: '#374151' }}>
                      {s.phone || '-'}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '0.8rem', color: '#374151' }}>
                        {s.course && s.course !== 'Pending' ? s.course : '-'}
                      </Typography>
                      {s.country && s.country !== 'TBD' && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#9CA3AF' }}>{s.country}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={s.status} />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#F59E0B' }}>
                      {formatAmount(s.consultancyPayout)}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={s.consultancyPayoutStatus === 'paid' ? 'Paid' : 'Pending'}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          bgcolor:
                            s.consultancyPayoutStatus === 'paid'
                              ? alpha('#10B981', 0.1)
                              : alpha('#F59E0B', 0.1),
                          color:
                            s.consultancyPayoutStatus === 'paid' ? '#10B981' : '#F59E0B',
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Add Lead Dialog */}
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AddRounded /> Add New Lead
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '0.85rem', color: '#6B7280', mb: 2 }}>
            Enter the student's basic details. Your consultancy will be automatically linked.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Full Name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                error={!!formErrors.name}
                helperText={formErrors.name}
                autoFocus
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Phone Number"
                required
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                error={!!formErrors.phone}
                helperText={formErrors.phone}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDialogClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleAddLead} variant="contained" disabled={saving}>
            {saving ? 'Adding...' : 'Add Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ConsultancyPortalPage;
